import { Outlet, Link, createRootRoute, HeadContent, Scripts, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

import appCss from "../styles.css?url";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";


function NotFoundComponent() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-extrabold text-brand-navy">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Seite nicht gefunden</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Die gesuchte Seite existiert nicht oder wurde verschoben.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-brand-orange px-6 py-3 text-sm font-bold uppercase tracking-wider text-brand-orange-foreground hover:bg-brand-orange/90"
          >
            Zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Schober Transportservice – Transporte, Entrümpelungen & Räumungen" },
      {
        name: "description",
        content:
          "Schober Transportservice – Ihr kompetenter Partner für Transporte, Umzüge, Entrümpelungen, Räumungen, Demontagen und Grünschnitt in NÖ und Burgenland.",
      },
      { name: "author", content: "Patric Schober" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Schober Transportservice – Transporte, Entrümpelungen & Räumungen" },
      { name: "twitter:title", content: "Schober Transportservice – Transporte, Entrümpelungen & Räumungen" },
      { property: "og:description", content: "Transport, Umzug, Entrümpelung und Räumung mit Angebot in Wien, Niederösterreich, Burgenland und ganz Österreich." },
      { name: "twitter:description", content: "Transport, Umzug, Entrümpelung und Räumung mit Angebot in Wien, Niederösterreich, Burgenland und ganz Österreich." },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MovingCompany",
          "@id": "https://schober-transportservice.at/#business",
          name: "Schober Transportservice",
          image: "https://schober-transportservice.at/logo.png",
          url: "https://schober-transportservice.at/",
          telephone: "+43 680 23 08 700",
          email: "office@schober-transportservice.at",
          priceRange: "€€",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Heuweg 5/2",
            postalCode: "2721",
            addressLocality: "Bad Fischau",
            addressRegion: "NÖ",
            addressCountry: "AT",
          },
          geo: { "@type": "GeoCoordinates", latitude: 47.8333, longitude: 16.1667 },
          areaServed: ["Wien", "Niederösterreich", "Burgenland"],
          openingHoursSpecification: [
            { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday"], opens: "07:00", closes: "19:00" },
            { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "08:00", closes: "16:00" },
          ],
          sameAs: ["https://www.myhammer.at/"],
          aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "16", bestRating: "5" },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="de">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function ScrollToTop() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname]);
  return null;
}

function RootComponent() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <ScrollToTop />
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
